const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ABBREVIATION_PATTERN = /^[A-Za-z0-9_-]+$/;

export const CONFIG_FIELD_LABELS = {
  customerName: 'Customer Name',
  programName: 'Program Name',
  customerNameAbbreviation: 'Customer Abbreviation',
  clientName: 'Client Name',
  clientNameAbbreviation: 'Client Abbreviation',
  logoUrl: 'Logo URL',
  supportNumber: 'Support Number',
  faxNumberInbound: 'Inbound Fax Number',
  email: 'Email',
  isAutoAcceptEnabled: 'Auto Accept',
  isIntroFaxEnabled: 'Intro Fax',
  showSavingPrices: 'Show Prices',
  botCallAllowed: 'Bot Call',
  botTaskAllowed: 'Bot Task',
  botPlanName: 'Plan Name',
  faxTemplate: 'Fax Template',
  language: 'Language',
  populationType: 'Population Type',
  prescriberCallActivitiesEnabled: 'Prescriber Call Activities',
  pharmacyCallActivitiesEnabled: 'Pharmacy Call Activities',
  polypharmacyEnabled: 'Polypharmacy',
  deprescriptionConfirmationEnabled: 'Deprescription Confirmations',
  cobEnabled: 'COB',
  hasCustomSwitches: 'Custom Switches',
  isPrescriberEmailEnabled: 'Prescriber Emails',
  isRxAdvocateEnabled: 'RX Advocate',
  useNvpSourcePricing: 'Use Price Factors',
  excludeStarSwitches: 'Exclude STARS Medications',
  excludeStarDeprescriptions: 'Hide Deprescribe Suggestions',
};

export const normalizePhoneDigits = (value) => {
  if (!value) return '';
  return String(value).replace(/[^0-9]/g, '').slice(0, 10);
};

export const normalizeFormStateForCompare = (state) => {
  if (!state) return state;
  return {
    ...state,
    supportNumber: normalizePhoneDigits(state.supportNumber),
    faxNumberInbound: normalizePhoneDigits(state.faxNumberInbound),
    botPlanName: state.botPlanName ?? '',
    botCallAllowed: state.botCallAllowed === true,
    botTaskAllowed: state.botTaskAllowed === true,
    showSavingPrices: state.showSavingPrices !== false,
    isAutoAcceptEnabled: state.isAutoAcceptEnabled === true,
    isIntroFaxEnabled: state.isIntroFaxEnabled === true,
    prescriberCallActivitiesEnabled: state.prescriberCallActivitiesEnabled === true,
    pharmacyCallActivitiesEnabled: state.pharmacyCallActivitiesEnabled === true,
    useNvpSourcePricing: state.useNvpSourcePricing === true,
    excludeStarSwitches: state.excludeStarSwitches === true,
    excludeStarDeprescriptions: state.excludeStarDeprescriptions === true,
    polypharmacyEnabled: state.polypharmacyEnabled === true,
    deprescriptionConfirmationEnabled: state.deprescriptionConfirmationEnabled === true,
    cobEnabled: state.cobEnabled === true,
    isRxAdvocateEnabled: state.isRxAdvocateEnabled === true,
    hasCustomSwitches: state.hasCustomSwitches === true,
    isPrescriberEmailEnabled: state.isPrescriberEmailEnabled === true,
  };
};

export const isFormDirty = (currentState, originalState) => {
  if (!currentState || !originalState) return false;
  const current = normalizeFormStateForCompare(currentState);
  const original = normalizeFormStateForCompare(originalState);
  const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
  return [...keys].some((key) => current[key] !== original[key]);
};

export const validateRequiredTrim = (value, label) => {
  if (!value || !String(value).trim()) {
    return `${label} is required.`;
  }
  return null;
};

export const validateEmail = (value) => {
  if (!value || !String(value).trim()) return null;
  if (!EMAIL_PATTERN.test(String(value).trim())) {
    return 'Enter a valid email address.';
  }
  return null;
};

export const validatePhone = (value, label) => {
  if (!value || !String(value).trim()) return null;
  const digits = normalizePhoneDigits(value);
  if (digits.length !== 10) {
    return `${label} must be a 10-digit number.`;
  }
  return null;
};

export const validateAbbreviation = (value, label) => {
  const requiredError = validateRequiredTrim(value, label);
  if (requiredError) return requiredError;
  const trimmed = String(value).trim();
  if (trimmed.length > 10) {
    return `${label} must be 10 characters or fewer.`;
  }
  if (!ABBREVIATION_PATTERN.test(trimmed)) {
    return `${label} may only contain letters, numbers, hyphens, and underscores.`;
  }
  return null;
};

export const validateBotCallingSelection = (formState, { botCallingEnabled } = {}) => {
  if (botCallingEnabled && !formState.botCallAllowed && !formState.botTaskAllowed) {
    return 'Enable Tasks or Calls when Bot Calling is on.';
  }
  return null;
};

export const validateLogoUrl = (value) =>
  validateRequiredTrim(value, 'Logo URL');

export const validateCustomerCreateForm = (formState, options = {}) => {
  const errors = {};
  const customerNameError = validateRequiredTrim(formState.customerName, 'Customer Name');
  if (customerNameError) errors.customerName = customerNameError;

  const programNameError = validateRequiredTrim(formState.programName, 'Program Name');
  if (programNameError) errors.programName = programNameError;

  const abbreviationError = validateAbbreviation(formState.customerNameAbbreviation, 'Customer Abbreviation');
  if (abbreviationError) errors.customerNameAbbreviation = abbreviationError;

  const logoError = validateLogoUrl(formState.logoUrl);
  if (logoError) errors.logoUrl = logoError;

  const emailError = validateEmail(formState.email);
  if (emailError) errors.email = emailError;

  const supportError = validatePhone(formState.supportNumber, 'Support Number');
  if (supportError) errors.supportNumber = supportError;

  const faxError = validatePhone(formState.faxNumberInbound, 'Inbound Fax Number');
  if (faxError) errors.faxNumberInbound = faxError;

  const botCallingError = validateBotCallingSelection(formState, options);
  if (botCallingError) errors.botCalling = botCallingError;

  return errors;
};

export const validateCustomerEditForm = (formState, options = {}) => {
  const errors = {};
  const logoError = validateLogoUrl(formState.logoUrl);
  if (logoError) errors.logoUrl = logoError;

  const emailError = validateEmail(formState.email);
  if (emailError) errors.email = emailError;

  const supportError = validatePhone(formState.supportNumber, 'Support Number');
  if (supportError) errors.supportNumber = supportError;

  const faxError = validatePhone(formState.faxNumberInbound, 'Inbound Fax Number');
  if (faxError) errors.faxNumberInbound = faxError;

  const botCallingError = validateBotCallingSelection(formState, options);
  if (botCallingError) errors.botCalling = botCallingError;

  return errors;
};

export const validateSelectRequired = (value, label) => {
  if (!value || !String(value).trim()) {
    return `${label} is required.`;
  }
  return null;
};

export const validateClientProgramDefaults = (formState) => {
  const errors = {};
  const languageError = validateSelectRequired(formState.language, 'Language');
  if (languageError) errors.language = languageError;

  const faxTemplateError = validateSelectRequired(formState.faxTemplate, 'Fax Template');
  if (faxTemplateError) errors.faxTemplate = faxTemplateError;

  const populationTypeError = validateSelectRequired(formState.populationType, 'Population Type');
  if (populationTypeError) errors.populationType = populationTypeError;

  return errors;
};

export const validateClientCreateForm = (formState) => {
  const errors = {};
  const clientNameError = validateRequiredTrim(formState.clientName, 'Client Name');
  if (clientNameError) errors.clientName = clientNameError;

  const programNameError = validateRequiredTrim(formState.programName, 'Program Name');
  if (programNameError) errors.programName = programNameError;

  const abbreviationError = validateAbbreviation(formState.clientNameAbbreviation, 'Client Abbreviation');
  if (abbreviationError) errors.clientNameAbbreviation = abbreviationError;

  const logoError = validateLogoUrl(formState.logoUrl);
  if (logoError) errors.logoUrl = logoError;

  Object.assign(errors, validateClientProgramDefaults(formState));

  const supportError = validatePhone(formState.supportNumber, 'Support Number');
  if (supportError) errors.supportNumber = supportError;

  const faxError = validatePhone(formState.faxNumberInbound, 'Inbound Fax Number');
  if (faxError) errors.faxNumberInbound = faxError;

  return errors;
};

export const validateClientEditForm = (formState) => {
  const errors = {};
  const programNameError = validateRequiredTrim(formState.programName, 'Program Name');
  if (programNameError) errors.programName = programNameError;

  const logoError = validateLogoUrl(formState.logoUrl);
  if (logoError) errors.logoUrl = logoError;

  Object.assign(errors, validateClientProgramDefaults(formState));

  const supportError = validatePhone(formState.supportNumber, 'Support Number');
  if (supportError) errors.supportNumber = supportError;

  const faxError = validatePhone(formState.faxNumberInbound, 'Inbound Fax Number');
  if (faxError) errors.faxNumberInbound = faxError;

  return errors;
};
